import React, { useEffect, useMemo, useState } from "react";
import { Button, ButtonGroup, Dialog, Icon } from "@blueprintjs/core";
import { faAws, faJira, faSlack } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mantine/core";

import { AppToaster, Link, LoadingView } from "@components";
import { useActions, useIntegrations, useOrg } from "@hooks";
import { Feature } from "@utils";

import { slugifyOrgName } from "@pages/dashboard/utils";
import {
  createIntegration,
  deleteIntegration,
  deleteSlackAppIntegration,
  deleteSlackAppNotificationMapping,
  fetchSlackAppIntegration,
  fetchSlackAppNotificationMappings,
  slackAppRedirect,
  testSlackAppNotificationMapping,
  updateIntegration,
} from "@pages/settings/api";
import { ActionPanelCard } from "@pages/settings/components/ActionsPanelCard";
import { IntegrationPanelCard } from "@pages/settings/components/IntegrationPanelCard";
import { IntegrationSlackAppCard } from "@pages/settings/components/IntegrationSlackAppCard";
import {
  Action,
  ActionChannel,
  ActionConfig,
  Integration,
  IntegrationType,
  SlackAppIntegration,
  SlackAppNotificationMapping,
  WebhookActionChannels,
} from "@pages/settings/types";

type Props = {
  loaded: boolean;
};

export const IntegrationsPanel: React.FC<Props> = ({ loaded }) => {
  const actionsQuery = useActions();
  const actions = useMemo(() => actionsQuery.data ?? [], [actionsQuery.data]);
  const integrationsQuery = useIntegrations();
  const integrations = useMemo(
    () => integrationsQuery.data ?? [],
    [integrationsQuery.data]
  );
  const [slackApp, setSlackApp] = useState<SlackAppIntegration>();
  const [slackAppNotificationMappings, setSlackAppNotificationMappings] =
    useState<SlackAppNotificationMapping[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [localActions, setLocalActions] = useState<Action[]>(actions);
  const [localIntegrations, setLocalIntegrations] =
    useState<Integration[]>(integrations);
  const org = useOrg();

  useEffect(() => {
    setLocalActions(actions);
  }, [actions]);

  useEffect(() => {
    setLocalIntegrations(integrations);
  }, [integrations]);

  useEffect(() => {
    if (!org) {
      throw new Error("No org found!");
    }

    // API call to fetch details about the Slack app integration
    fetchSlackAppIntegration(org.id)
      .then((response) => {
        setSlackApp({
          type: IntegrationType.SlackApp,
          ...response,
        });
      })
      .catch((err) => {
        if (err.statusCode === 404) {
          // exempt this error in this case
          return;
        }
        AppToaster.show({
          message: `Error fetching Slack app integration: ${err.body?.error}`,
          intent: "danger",
        });
      });
  }, [org]);

  useEffect(() => {
    if (!org) {
      throw new Error("No org found!");
    }

    if (!slackApp) {
      return;
    }

    // API call to fetch Slack App Notification Mappings
    fetchSlackAppNotificationMappings(org.id)
      .then((response) => {
        setSlackAppNotificationMappings(response.notification_mappings);
      })
      .catch((err) => {
        AppToaster.show({
          message: `Error fetching Slack app integration: ${err.body?.error}`,
          intent: "danger",
        });
      });
  }, [org, slackApp]);

  /**
   * Save integration (creating new integration if Integration object does not have id, updating if it does)
   * then calls CALLBACK
   */
  const saveIntegration: (
    integration: Integration,
    index: number,
    callback: (integration: Integration) => void,
    token?: string
  ) => void = useMemo(
    () =>
      (
        integration: Integration,
        _: number,
        callback: (integration: Integration) => void,
        integrationToken?: string
      ) => {
        if (integration === undefined || org === undefined) {
          return;
        }
        if (
          integrations.some(
            (i) => i.name === integration.name && i.id !== integration.id
          )
        ) {
          AppToaster.show({
            message: `Integrations must have a unique name`,
            intent: "danger",
          });
          return;
        }
        if (integration.id === undefined) {
          if (integrationToken === undefined) {
            AppToaster.show({
              message: `Integration must have token`,
              intent: "danger",
            });
            return;
          }
          // Create new integration
          createIntegration(org.id, integration, integrationToken)
            .then((newIntegration) => {
              AppToaster.show({
                message: `Created ${integration.name}`,
                intent: "success",
              });
              integrationsQuery.refetch();
              return newIntegration;
            })
            .then(callback)
            .catch((err) => {
              AppToaster.show({
                message: `Error saving ${integration.name}: ${err.body?.error}`,
                intent: "danger",
              });
            });
        } else {
          updateIntegration(org.id, integration, integrationToken)
            .then((newIntegration) => {
              AppToaster.show({
                message: `Updated ${integration.name}`,
                intent: "success",
              });
              integrationsQuery.refetch();
              return newIntegration;
            })
            .then(callback)
            .catch((err) => {
              AppToaster.show({
                message: `Error saving ${integration.name}: ${err.body?.error}`,
                intent: "danger",
              });
            });
        }
      },
    [org, integrations, integrationsQuery]
  );

  // Return function that deletes integration also removes integration at index from this.state.integrations
  const removeIntegration: (
    index: number
  ) => (integration: Integration) => void = useMemo(
    () => (index: number) => (integration: Integration) => {
      if (org === undefined) return;
      if (integration.id !== undefined) {
        deleteIntegration(org.id, integration.id)
          .then((deletedIntegration) => {
            let integrationsCopy = [...localIntegrations];
            integrationsCopy.splice(index, 1);
            setLocalIntegrations(integrationsCopy);
            return deletedIntegration;
          })
          .then(() => {
            AppToaster.show({
              message: `Deleted Integration`,
              intent: "success",
            });
            integrationsQuery.refetch();
          })
          .catch((err) => {
            AppToaster.show({
              message: `Error deleting integration: ${err.body?.error}`,
              intent: "danger",
            });
          });
      } else {
        let integrationsCopy = [...localIntegrations];
        integrationsCopy.splice(index, 1);
        setLocalIntegrations(integrationsCopy);
        integrationsQuery.refetch();
      }
    },
    [localIntegrations, setLocalIntegrations, integrationsQuery, org]
  );

  const testSlackAppMapping = (channel_id: string) => {
    if (org === undefined) return;
    if (slackApp === null) return;
    testSlackAppNotificationMapping(org.id, channel_id)
      .then((response) => {
        return response;
      })
      .then(() => {
        AppToaster.show({
          message: `Sent a notification to the Slack channel`,
          intent: "success",
        });
      })
      .catch((err) => {
        AppToaster.show({
          message: `Error sending notification to the Slack channel: ${err.body?.error}`,
          intent: "danger",
        });
      });
  };

  const removeSlackAppMapping = (channel_id: string) => {
    if (org === undefined) return;
    if (slackApp === null) return;
    deleteSlackAppNotificationMapping(org.id, channel_id)
      .then((response) => {
        setSlackAppNotificationMappings(response.notification_mappings);
        return response;
      })
      .then(() => {
        AppToaster.show({
          message: `Successfully removed a notification mapping`,
          intent: "success",
        });
      })
      .catch((err) => {
        AppToaster.show({
          message: `Error removing the notification mapping: ${err.body?.error}`,
          intent: "danger",
        });
      });
  };

  // Return the function that deletes the Slack app installation for the current deployment
  const removeSlackApp = () => {
    if (org === undefined) return;
    if (slackApp === null) return;
    deleteSlackAppIntegration(org.id)
      .then((response) => {
        setSlackApp(undefined);
        return response;
      })
      .then(() => {
        AppToaster.show({
          message: `Deleted integration`,
          intent: "success",
        });
      })
      .catch((err) => {
        AppToaster.show({
          message: `Error deleting integration: ${err.body?.error}`,
          intent: "danger",
        });
      });
  };

  /**
   * Handles the Slack app auth flow
   */
  const slackAppAuthFlow: () => void = useMemo(
    () => () => {
      if (org === undefined) return;
      slackAppRedirect(org.id)
        .then((htmlResp) => {
          window.location.replace(htmlResp.redirect_url);
        })
        .catch((err) => {
          AppToaster.show({
            message: `Error installing Semgrep Slack app: ${err.body?.error}`,
            intent: "danger",
          });
        });
    },
    [org]
  );

  const addAction = useMemo(
    () => (channel: ActionChannel) => {
      let config: ActionConfig = { url: "" };
      if (channel === ActionChannel.S3) {
        config = {};
      } else if (WebhookActionChannels.includes(channel)) {
        config = {
          url: "",
        };
      } else {
        config = {
          email: "",
        };
      }
      setLocalActions([
        ...localActions,
        {
          channel,
          config,
          is_enabled: true,
          name: "",
          policy_slugs: [],
        },
      ]);
      setIsCreateDialogOpen(false);
    },
    [localActions, setLocalActions, setIsCreateDialogOpen]
  );

  const addEmailAction = useMemo(
    () => () => {
      addAction(ActionChannel.Email);
    },
    [addAction]
  );

  const addS3Action = useMemo(
    () => () => {
      addAction(ActionChannel.S3);
    },
    [addAction]
  );

  const addWebhookAction = useMemo(
    () => () => {
      addAction(ActionChannel.Webhook);
    },
    [addAction]
  );

  const addJiraIntegration = useMemo(
    () => () => {
      setLocalIntegrations([
        ...localIntegrations,
        {
          name: "",
          type: IntegrationType.Jira,
          url: "",
          email: "",
          meta: {},
        },
      ]);
      setIsCreateDialogOpen(false);
    },
    [localIntegrations, setLocalIntegrations, setIsCreateDialogOpen]
  );

  const openCreateDialog = useMemo(
    () => () => setIsCreateDialogOpen(true),
    [setIsCreateDialogOpen]
  );
  const closeCreateDialog = useMemo(
    () => () => setIsCreateDialogOpen(false),
    [setIsCreateDialogOpen]
  );

  if (!loaded) return <LoadingView />;

  return (
    <div className="panel">
      <div className="panel-main">
        <Dialog
          onClose={closeCreateDialog}
          title="Create New Integration Channel"
          isOpen={isCreateDialogOpen}
        >
          <ButtonGroup vertical={true}>
            <Button
              data-testid="slack-button"
              large={true}
              icon={<FontAwesomeIcon icon={faSlack} size="2x" />}
              onClick={slackAppAuthFlow}
            >
              {" "}
              Slack{" "}
            </Button>
            <Button
              data-testid="email-button"
              large={true}
              icon={<Icon icon="envelope" size={30} color={"black"} />}
              onClick={addEmailAction}
            >
              Email
            </Button>
            {Feature.hasS3Integration({ org }) && (
              <Tooltip label="Send findings to S3" withArrow withinPortal>
                <div>
                  <Button
                    large
                    onClick={addS3Action}
                    icon={<FontAwesomeIcon icon={faAws} />}
                  >
                    S3
                  </Button>
                </div>
              </Tooltip>
            )}
            <Button
              large
              onClick={addJiraIntegration}
              icon={<FontAwesomeIcon icon={faJira} size={"lg"} />}
              disabled={!Feature.hasTeamsTier({ org })}
              data-testid="jira-button"
            >
              {Feature.hasTeamsTier({ org }) ? (
                <span>Jira</span>
              ) : (
                <span>
                  Jira (
                  <u>
                    <Link
                      to={`/orgs/${slugifyOrgName(
                        org?.name ?? "-"
                      )}/settings/payment`}
                    >
                      upgrade
                    </Link>
                  </u>
                  )
                </span>
              )}
            </Button>
            <Button
              large
              onClick={addWebhookAction}
              disabled={!Feature.hasTeamsTier({ org })}
              data-testid="webhook-button"
            >
              {Feature.hasTeamsTier({ org }) ? (
                <span>Webhook</span>
              ) : (
                <span>
                  Webhook (
                  <u>
                    <Link
                      to={`/orgs/${slugifyOrgName(
                        org?.name ?? "-"
                      )}/settings/payment`}
                    >
                      upgrade
                    </Link>
                  </u>
                  )
                </span>
              )}
            </Button>
          </ButtonGroup>
        </Dialog>
        <div className="notifications" style={{ maxWidth: "800px" }}>
          {localActions.length > 0 ||
          localIntegrations.length > 0 ||
          slackApp ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                intent="primary"
                onClick={openCreateDialog}
                children="Add Integration"
              />
            </div>
          ) : (
            <>
              <h3>
                Semgrep can integrate with Slack or email accounts to send you
                notifications
              </h3>
              <Button
                onClick={openCreateDialog}
                intent="primary"
                icon="add"
                children="Set up First Integration"
                data-testid="setup-integration-button"
              />
            </>
          )}
          {localActions.length > 0 &&
            localActions.map((action, index) => (
              <ActionPanelCard
                key={action.id || "key" + index}
                inheritedAction={action}
              />
            ))}
          {localIntegrations.length > 0 &&
            localIntegrations.map((integration, index) => (
              <IntegrationPanelCard
                key={integration.id || "key" + index}
                save={saveIntegration}
                inheritedIntegration={integration}
                onDelete={removeIntegration(index)}
                index={index}
              />
            ))}
          {slackApp && (
            <IntegrationSlackAppCard
              integration={slackApp}
              mappings={slackAppNotificationMappings}
              onDelete={removeSlackApp}
              onUpdate={slackAppAuthFlow}
              testMapping={testSlackAppMapping}
              removeMapping={removeSlackAppMapping}
            />
          )}
          <br />
        </div>
      </div>
    </div>
  );
};
